import { useRef } from 'react';
import { join } from '../../../lib/Array.lib';
import fetchCandidateResume from '../../../lib/Candidate/fetchCandidateResume.async';
import Core from '../../../lib/Core';
import globalErrorHandler from '../../../lib/Error/globalErrorHandler.fun';
import {
  Fun
} from '../../../lib/Function.lib';
import {
  getShortURL,
  NOT,
  validateURL
} from '../../../lib/GenericTools.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  Obj
} from '../../../lib/Object.lib';
import {
  joinKeyName,
  Str
} from '../../../lib/String.lib';
import formatURL from '../../../lib/tools/formatURL';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import {
  Html
} from '../../Layout/Wrappers/Html';
import Icon from '../../Layout/Wrappers/Icon';
import Typography from '../../Layout/Wrappers/Typography';
import ResumeMatch from '../../Shared/ResumeMatch';
import { fetchTagLabelEmployerSkills } from './Libraries/EmployerSkills.lib';
import { fetchTagLabelJobSkills } from './Libraries/JobSkills.lib';

export function getControllerCandidateDocumentPreview() {
  return Obj(Core.getKeyValue('CandidateDocumentPreviewController'));
}

export function setControllerCandidateDocumentPreview(controller) {
  Core.setKeyValue('CandidateDocumentPreviewController', controller);
}

export default function CandidateDocumentPreview({
  onLoad = Fun,
  candidate = {},
  jobId = '',
  employerId = '',
  ...props
}) {
  const mem = useRef({});
  const [state, updateState] = useState({
    source: 'resume'
  });
  const {
    resumeTxtUrl,
    htmlResumeUrl,
    gitHubURL,
    linkedInURL,
    stackoverflowUrl,
    otherLinks = '',
  } = candidate;
  const resumeFiles = [
    {
      key: 'linkedin',
      url: formatURL(linkedInURL),
      type: 'SITE',
      label: <>LinkedIn</>
    },
    {
      key: 'resume',
      url: state.resumePdfUrl,
      type: 'PDF',
      label: 'PDF'
    },
    {
      key: 'highlights',
      url: state.resumeHighlightsUrl,
      type: 'PDF',
      label: 'Highlights'
    },
    {
      key: 'text',
      url: formatURL(resumeTxtUrl),
      type: 'TXT',
      label: 'Skill Text'
    },
    {
      key: 'html',
      url: formatURL(htmlResumeUrl),
      type: 'HTML',
      label: 'HTML'
    },
    {
      key: 'github',
      url: formatURL(gitHubURL),
      type: 'SITE',
      label: 'GitHub'
    },
    {
      key: 'stack_overflow',
      url: formatURL(stackoverflowUrl),
      type: 'SITE',
      label: 'Stack overflow'
    },
    [
      ...String(otherLinks || '')
        .split(/\n| - |\s|(,|;|\|)\s?/)
        .map((url, index) => ({
          key: joinKeyName(['site', index]),
          url: formatURL(url),
          type: 'SITE',
          label: getShortURL(url)
        }))
        .filter(({ url }) => validateURL(url))
    ][0]
  ].filter(
    (resume) => (
      !!resume &&
      (
        (resume.key === 'highlights') ||
        !!resume.url
      )
    )
  );
  (async () => {
    /**
     * @note
     * This is loading async the resume and the highlights.
     * We can not useo the Once component with a promise,
     * because the display of HTML elements is controlled
     * acording to the state of the loading of the resume,
     * but this render code is executed no matter ccs rules
     */
    if (
      NOT(candidate.id)
      || (
        (candidate.id === mem.current.prev) && (
          mem.current.busy
          || mem.current.ready
          || mem.current.error
        )
      )
    ) { return; }
    mem.current.busy = true;
    mem.current.prev = candidate.id;
    try {
      await updateState({
        loading: true,
        source: 'resume',
        error: false
      });
      const localUrlCandidateResumePdf = await fetchCandidateResume({
        key: 'pdf',
        type: 'blob',
        candidate,
        source: 'CandidateDocumentPreview__pdf'
      }).catch((error) => {
        Core.showError('The resume file is not available');
        return null;
      });
      fetchCandidateResume({
        key: 'highlighted',
        type: 'blob',
        candidate,
        jobId,
        employerId,
        source: 'CandidateDocumentPreview__highlights',
      }).then(
        (localUrlCandidateResumeHighlights) => updateState({
          resumeHighlightsUrl: localUrlCandidateResumeHighlights
        })
      ).catch(
        (error) => updateState({ error })
      );
      await updateState({
        resumePdfUrl: localUrlCandidateResumePdf,
        jobSkills: join(await fetchTagLabelJobSkills({ jobId })),
        employerSkills: join(await fetchTagLabelEmployerSkills({ employerId })),
        loading: false,
      });
      setTimeout(onLoad, 100);
      mem.current.ready = true;
    }
    catch (error) {
      mem.current.error = error;
      await updateState({
        loading: false
      });
      globalErrorHandler(error);
    }
    mem.current.busy = false;
  })();
  const VALUE__LOCAL_URL = Str(
    ['resume', 'highlights'].includes(state.source) &&
    Obj(
      resumeFiles.find(
        ({ key }) => (key === state.source)
      )
    ).url
  );
  setControllerCandidateDocumentPreview({
    state,
    updateState
  })
  console.debug('CandidateDocumentPreview', state);
  return (
    <Box column h100 w100 role="CandidateDocumentPreview">
      <Box
        row
        w100
        scrollX
        noWrap
        alignCenter
        role="CandidateDocumentPreview__Header"
        className="p-1"
        style={{ minHeight: 42 }}
      >
        {(() => {
          return resumeFiles.map((resume, index) =>
            (
              resume.type === 'TXT'
            ) ? (
              <ResumeMatch
                key={joinKeyName(['document_preview', index, resume.key])}
                candidateResume={resume.url}
                jobSkills={Str(state.jobSkills)}
                employerSkills={Str(state.employerSkills)}
                element={(cb) => {
                  return (
                    <Button
                      small
                      mr1
                      minW80
                      variant={
                        (state.source === resume.key)
                          ? 'outlined'
                          : 'standard'
                      }
                      className="p-0 px-1"
                      onClick={(ev) => cb()}
                    >
                      {resume.label}
                    </Button>
                  );
                }}
                onClick={(resumeTXT) => {
                  updateState({
                    source: resume.key,
                    resumeTXT
                  });
                }}
              />
            ) : (
              <Button
                small
                mr1
                minW80
                key={joinKeyName(['document_preview', index, resume.key])}
                variant={
                  (state.source === resume.key)
                    ? 'outlined'
                    : 'standard'
                }
                className="p-0 px-1"
                onClick={async (event) => {
                  if (resume.type === 'SITE') {
                    Core.openPopUp(resume.url);
                  }
                  else if (resume.key === 'html') {
                    fetchCandidateResume({
                      url: resume.url,
                      source: 'CandidateDocumentPreview > resumeHTML',
                    }).then(
                      (resumeHTML = '') => {
                        if (resumeHTML) {
                          updateState({
                            source: resume.key,
                            resumeHTML
                          });
                        }
                      }
                    );
                  }
                  else {
                    updateState({ source: resume.key });
                  }
                }}
                disabled={
                  (resume.key === 'highlights') &&
                  (state.loading || state.error)
                }
              >
                <Typography mr>
                  {resume.label}
                </Typography>
                {
                  (resume.key === 'highlights') &&
                  (state.loading || state.error) &&
                  (
                    <Icon outlined
                      icon={state.loading ? 'hourglass_empty' : 'error'}
                      className='icon16 help'
                      title={
                        String(state.error) ||
                        (state.loading && 'Loading...')
                      }
                    />
                  )
                }
              </Button>
            )
          );
        })()}
      </Box>
      <Box
        column
        h100
        scrollY
        role="CandidateDocumentPreview__Content"
        className="flex-align-left-top"
      >
        {(state.source === 'text') && state.resumeTXT &&
          <Html column={false}
            role="CandidateDocumentPreview__TXT"
            className="flex-wrap flex-align-left-top break-all p-2"
          >
            {state.resumeTXT}
          </Html>
        }
        {(state.source === 'html') && state.resumeHTML &&
          <Html
            role="CandidateDocumentPreview__HTML"
            className="flex-wrap flex-align-left-top break-all p-2"
          >
            {state.resumeHTML}
          </Html>
        }
        {VALUE__LOCAL_URL &&
          <iframe
            title='candidate_document_preview__iframe'
            src={`${VALUE__LOCAL_URL}#view=fitH&toolbar=1`}
            type="application/pdf"
            width="100%"
            height="100%"
            className="h-100"
          />
        }
      </Box>
    </Box>
  );
}
